import { PricingCheckBox } from "@components"
import { TechnologyCard, TechnologyCardList } from "@types"
import * as React from "react"

interface PricingCardProps {
  technologyCard?: TechnologyCard
  setTechnologyCards: Function
  cardIndex: number
  compose: {
    heading: string
    description: string
  }
  setCompose: Function
}
export const PricingCard = ({
  technologyCard,
  setTechnologyCards,
  cardIndex,
  compose,
  setCompose,
}: PricingCardProps) => {
  const [checkedItems, setCheckedItems] = React.useState(
    (technologyCard?.technologyCards ?? []).map((item: TechnologyCardList) => ({
      ...item,
      isChecked: false,
      totalItems: item.sideText?.includes("day")
        ? 8
        : item.sideText?.includes("week")
          ? 40
          : item.sideText?.includes("Month")
            ? 160
            : 1,
      totalPrice: (item?.totalItems ?? 0) * Number(item.price),
    }))
  )
  // Call this function whenever you toggle the `isChecked` property
  const updateTechnologyCardTotalPrice = (cardIndex: number) => {
    setTechnologyCards((prevCards: TechnologyCard[]) => {
      if (!prevCards) return prevCards

      const updatedCards = [...prevCards]
      const targetCard = updatedCards[cardIndex]
      // Check if targetCard and its technologyCards are defined
      if (targetCard && targetCard.technologyCards) {
        // Recalculate totalPrice based on checked items and their nested options
        targetCard.totalPrice = targetCard.technologyCards.reduce(
          (acc, item) => {
            // Include the price of the main item if it's checked
            const itemTotal = item.isChecked ? (item.totalPrice ?? 0) : 0

            // Add the prices of the nested options
            const optionsTotal =
              item.cardSubSectionOptions?.reduce((optionAcc, section) => {
                return (
                  section.multiOptions?.reduce((multiAcc, option) => {
                    return (
                      multiAcc +
                      (option.isChecked ? Number(option.totalPrice) || 0 : 0)
                    )
                  }, optionAcc) || optionAcc
                ) // Accumulate checked option prices
              }, 0) || 0 // Fallback to 0 if no options

            return acc + itemTotal + optionsTotal // Return the accumulated total
          },
          0
        )

        // Recalculate totalItems based on checked items and their nested options
        targetCard.totalItems = targetCard.technologyCards.reduce(
          (acc, item) => {
            // Include the items of the main item if it's checked
            const itemTotal =
              item.isChecked && Number(item.price) > 0
                ? (item.totalItems ?? 0)
                : 0

            // Add the items of the nested options
            const optionsTotal =
              item.cardSubSectionOptions?.reduce(
                (optionAcc: number, section: any) => {
                  if (section.isRadioOptions !== true)
                    return (
                      section.multiOptions?.reduce(
                        (multiAcc: number, option: any) => {
                          return (
                            multiAcc +
                            (option.isChecked
                              ? Number(option.totalItems) || 0
                              : 0)
                          )
                        },
                        optionAcc
                      ) || optionAcc
                    ) // Accumulate checked option prices
                },
                0
              ) || 0 // Fallback to 0 if no options

            return acc + itemTotal + optionsTotal // Return the accumulated total
          },
          0
        )

        // Recalculate totalMembers based on checked items and their nested options
        targetCard.members = targetCard.technologyCards.reduce((acc, item) => {
          // Include the members of the main item if it's checked
          const itemTotal =
            item.isChecked && Number(item.price) > 0 ? (item.members ?? 0) : 0

          // Add the members of the nested options
          const optionsTotal =
            item.cardSubSectionOptions?.reduce(
              (optionAcc: number, section: any) => {
                if (section.isRadioOptions !== true)
                  return (
                    section.multiOptions?.reduce(
                      (multiAcc: number, option: any) => {
                        return (
                          multiAcc +
                          (option.isChecked ? Number(option.members) || 0 : 0)
                        )
                      },
                      optionAcc
                    ) || optionAcc
                  ) // Accumulate checked option prices
              },
              0
            ) || 0 // Fallback to 0 if no options

          return acc + itemTotal + optionsTotal // Return the accumulated total
        }, 0)

        // Recalculate expectedDays based on checked items and their nested options
        targetCard.expectedDays = targetCard.technologyCards.reduce(
          (acc, item) => {
            // Include the expectedDays of the main item if it's checked
            const itemTotal =
              item.isChecked && Number(item.price) > 0
                ? (item.expectedDays ?? 0)
                : 0

            // Add the expectedDays of the nested options
            const optionsTotal =
              item.cardSubSectionOptions?.reduce((optionAcc, section: any) => {
                if (section.isRadioOptions !== true)
                  return (
                    section.multiOptions?.reduce(
                      (multiAcc: number, option: any) => {
                        return (
                          multiAcc +
                          (option.isChecked
                            ? Number(option.expectedDays) || 0
                            : 0)
                        )
                      },
                      optionAcc
                    ) || optionAcc
                  ) // Accumulate checked option prices
              }, 0) || 0 // Fallback to 0 if no options

            return acc + itemTotal + optionsTotal // Return the accumulated total
          },
          0
        )
      }

      // Apply the logic for members and expectedDays to each technology card and nested options
      targetCard.technologyCards =
        targetCard?.technologyCards &&
        targetCard?.technologyCards.map((card) => {
          const totalItems = card.totalItems ?? 0
          let cardMembers = 1
          let cardExpectedDays = 2

          if (totalItems > 6) {
            cardMembers = 3
            cardExpectedDays = 4
          } else if (totalItems >= 5) {
            cardMembers = 2
            cardExpectedDays = 2
          }

          const updatedCardSubSectionOptions = card.cardSubSectionOptions?.map(
            (section) => {
              const updatedMultiOptions = section.multiOptions?.map(
                (option) => {
                  const optionTotalItems = option.totalItems ?? 0
                  let optionMembers = 1
                  let optionExpectedDays = 2

                  if (optionTotalItems > 6) {
                    optionMembers = 3
                    optionExpectedDays = 4
                  } else if (optionTotalItems >= 5) {
                    optionMembers = 2
                    optionExpectedDays = 2
                  }

                  return {
                    ...option,
                    members: optionMembers,
                    expectedDays: optionExpectedDays,
                  }
                }
              )

              return {
                ...section,
                multiOptions: updatedMultiOptions,
              }
            }
          )

          return {
            ...card,
            members: cardMembers,
            expectedDays: cardExpectedDays,
            cardSubSectionOptions: updatedCardSubSectionOptions,
          }
        })

      return updatedCards
    })
  }

  const handleToggleItemCheck = (cardIndex: number, itemIndex: number) => {
    setTechnologyCards((prevCards: TechnologyCard[]) => {
      if (!prevCards) return prevCards

      const updatedCards = [...prevCards]
      const targetCard = updatedCards[cardIndex]

      // Check if targetCard and its technologyCards are defined
      if (targetCard && targetCard.technologyCards) {
        const targetItem = targetCard.technologyCards[itemIndex]

        // Check if targetItem exists
        if (targetItem) {
          // First, loop through all technologyCards for the targetCard
          targetCard.technologyCards.forEach((item: TechnologyCardList) => {
            if (
              targetItem.sideText?.includes("day") &&
              (item.sideText?.includes("week") ||
                item.sideText?.includes("Month") ||
                item.title?.includes("Number of Hours"))
            ) {
              // Hide other items for "day"
              item.isHidden = true
              item.isChecked = false
            } else if (
              targetItem.sideText?.includes("week") &&
              (item.sideText?.includes("day") ||
                item.sideText?.includes("Month") ||
                item.title?.includes("Number of Hours"))
            ) {
              // Hide other items for "week"
              item.isHidden = true
              item.isChecked = false
            } else if (
              targetItem.sideText?.includes("Month") &&
              (item.sideText?.includes("week") ||
                item.sideText?.includes("day") ||
                item.title?.includes("Number of Hours"))
            ) {
              // Hide other items for "Month"
              item.isHidden = true
              item.isChecked = false
            } else if (
              targetItem.title?.includes("Number of Hours") &&
              (item.sideText?.includes("week") ||
                item.sideText?.includes("day") ||
                item.sideText?.includes("Month"))
            ) {
              // Hide other items for "Number of Hours"
              item.isHidden = true
              item.isChecked = false
            } else if (item === targetItem) {
              // For the clicked item, toggle its checked state and ensure it is visible
              item.isHidden = false
              item.isChecked = !item.isChecked
            }
          })

          // Check if none of the items are checked
          const noItemsChecked = targetCard.technologyCards.every(
            (item) => item.isChecked === false
          )

          // If no items are checked, ensure all items are visible
          if (noItemsChecked) {
            targetCard.technologyCards.forEach((item) => {
              item.isHidden = false
            })
          }

          // Update total price and total items for the specific technologyCard
          targetItem.totalPrice = targetItem.isChecked
            ? (targetItem.totalItems ?? 0) * Number(targetItem.price)
            : 0

          updateTechnologyCardTotalPrice(cardIndex)
        }
      }

      return updatedCards
    })
  }

  const increase = (listIndex: number, category: string) => {
    const updatedCheckedItems = [...checkedItems]

    // Initialize variables for totalItems and totalPrice
    let newTotalItems = updatedCheckedItems[listIndex]?.totalItems || 0
    let days = Number(updatedCheckedItems[listIndex]?.day) || 0
    let weeks = Number(updatedCheckedItems[listIndex]?.week) || 0
    let months = Number(updatedCheckedItems[listIndex]?.month) || 0
    let words = Number(updatedCheckedItems[listIndex]?.words) || 0
    let seo = Number(updatedCheckedItems[listIndex]?.seo) || 0
    let marketing = Number(updatedCheckedItems[listIndex]?.marketing) || 0

    // Determine whether to use custom logic for day/week/month or default logic
    if (category.includes("day")) {
      days += 1
      newTotalItems = days * 8 // Each day adds 8 items
    } else if (category.includes("week")) {
      weeks += 1
      newTotalItems = weeks * 40 // Each week adds 40 items
    } else if (category.includes("Month")) {
      months += 1
      newTotalItems = months * 160 // Each month adds 160 items
    } else if (category.includes("words")) {
      words += 500
      newTotalItems += 1
    } else if (category.includes("seo")) {
      seo += 5
      newTotalItems += 1
    } else if (category.includes("marketing")) {
      marketing += 5
      newTotalItems += 1
    } else {
      // Default behavior: increment totalItems by 1
      newTotalItems += 1
    }
    // Get the price of the current item
    const itemPrice = Number(updatedCheckedItems[listIndex]?.price)

    // Increase the totalItems
    updatedCheckedItems[listIndex] = {
      ...updatedCheckedItems[listIndex],
      totalItems: newTotalItems,
      day: days,
      week: weeks,
      month: months,
      words: words,
      seo: seo,
      marketing: marketing,
    }
    Number(updatedCheckedItems[listIndex]?.price)
    // Update the technologyCards by adding the price of one item
    setTechnologyCards((prevTechnologyCards: TechnologyCard[]) =>
      prevTechnologyCards.map((card) =>
        card === technologyCard
          ? {
              ...card,
              technologyCards: card?.technologyCards?.map((item, index) => ({
                ...item,
                totalPrice:
                  listIndex === index
                    ? (newTotalItems ?? 0) * itemPrice
                    : item.totalPrice,
                totalItems:
                  listIndex === index ? newTotalItems : (item?.totalItems ?? 0),
                day: listIndex === index ? days : (item?.day ?? 0),
                week: listIndex === index ? weeks : (item?.week ?? 0),
                month: listIndex === index ? months : (item?.month ?? 0),
                words: listIndex === index ? words : (item?.words ?? 0),
                seo: listIndex === index ? seo : (item?.seo ?? 0),
                marketing:
                  listIndex === index ? marketing : (item?.marketing ?? 0),
              })),
            }
          : card
      )
    )

    // Update checkedItems
    setCheckedItems(updatedCheckedItems)
    const targetCard = technologyCard
    if (targetCard) {
      // Update the total price for the specific technologyCard
      updateTechnologyCardTotalPrice(cardIndex)
    }
  }

  const decrease = (listIndex: number, category: string) => {
    const updatedCheckedItems = [...checkedItems]

    // Get the current item's values for day, week, and month
    let days = Number(updatedCheckedItems[listIndex]?.day) || 0
    let weeks = Number(updatedCheckedItems[listIndex]?.week) || 0
    let months = Number(updatedCheckedItems[listIndex]?.month) || 0
    let newTotalItems = updatedCheckedItems[listIndex]?.totalItems || 0
    let words = Number(updatedCheckedItems[listIndex]?.words) || 0
    let seo = Number(updatedCheckedItems[listIndex]?.seo) || 0
    let marketing = Number(updatedCheckedItems[listIndex]?.marketing) || 0

    // Prevent decrementing below the minimum value
    if (category.includes("day") && days > 1) {
      days -= 1
      newTotalItems = days * 8 // Each day represents 8 items
    } else if (category.includes("week") && weeks > 1) {
      weeks -= 1
      newTotalItems = weeks * 40 // Each week represents 40 items
    } else if (category.includes("Month") && months > 1) {
      months -= 1
      newTotalItems = months * 160 // Each month represents 160 items
    } else if (category.includes("words") && words > 1) {
      words -= 500 // Each words
      newTotalItems -= 1
    } else if (category.includes("seo") && seo > 1) {
      seo -= 5
      newTotalItems -= 1
    } else if (category.includes("marketing") && marketing > 1) {
      marketing -= 5
      newTotalItems -= 1
    } else if (
      !category.includes("day") &&
      !category.includes("week") &&
      !category.includes("Month") &&
      newTotalItems > 1
    ) {
      // Default behavior: decrement totalItems by 1
      newTotalItems -= 1
    }

    // Ensure that we don't update if values are already at their minimum
    if (
      (category.includes("day") && days < 1) ||
      (category.includes("week") && weeks < 1) ||
      (category.includes("Month") && months < 1) ||
      (!category.includes("day") &&
        !category.includes("week") &&
        !category.includes("Month") &&
        newTotalItems < 1)
    ) {
      return // Exit the function to prevent invalid updates
    }

    // Get the price of the current item
    const itemPrice = Number(updatedCheckedItems[listIndex]?.price)

    // Update the checkedItems array
    updatedCheckedItems[listIndex] = {
      ...updatedCheckedItems[listIndex],
      totalItems: newTotalItems,
      day: days,
      week: weeks,
      month: months,
      words: words,
      seo: seo,
      marketing: marketing,
    }

    // Update the technologyCards by subtracting the price of one item
    setTechnologyCards((prevTechnologyCards: TechnologyCard[]) =>
      prevTechnologyCards.map((card) =>
        card === technologyCard
          ? {
              ...card,
              technologyCards: card?.technologyCards?.map((item, index) => ({
                ...item,
                totalPrice:
                  listIndex === index
                    ? (newTotalItems ?? 0) * itemPrice
                    : item.totalPrice,
                totalItems:
                  listIndex === index ? newTotalItems : (item?.totalItems ?? 0),
                day: listIndex === index ? days : (item?.day ?? 0),
                week: listIndex === index ? weeks : (item?.week ?? 0),
                month: listIndex === index ? months : (item?.month ?? 0),
                words: listIndex === index ? words : (item?.words ?? 0),
                seo: listIndex === index ? seo : (item?.seo ?? 0),
                marketing:
                  listIndex === index ? marketing : (item?.marketing ?? 0),
              })),
            }
          : card
      )
    )

    // Update checkedItems
    setCheckedItems(updatedCheckedItems)

    // Update the total price if the card is checked
    const targetCard = technologyCard
    if (targetCard) {
      updateTechnologyCardTotalPrice(cardIndex)
    }
  }
  const updateTotalItems = (
    listIndex: number,
    newTotalItems: number,
    category: string
  ) => {
    const updatedCheckedItems = [...checkedItems]

    // Initialize variables for day, week, and month
    let days = Number(updatedCheckedItems[listIndex]?.day) || 0
    let weeks = Number(updatedCheckedItems[listIndex]?.week) || 0
    let months = Number(updatedCheckedItems[listIndex]?.month) || 0

    // Update day/week/month based on the new totalItems
    if (category.includes("day")) {
      days = Math.ceil(newTotalItems / 8) // Each day represents 8 items
    } else if (category.includes("week")) {
      weeks = Math.ceil(newTotalItems / 40) // Each week represents 40 items
    } else if (category.includes("Month")) {
      months = Math.ceil(newTotalItems / 160) // Each month represents 160 items
    }

    // Update the checkedItems array
    updatedCheckedItems[listIndex] = {
      ...updatedCheckedItems[listIndex],
      totalItems: newTotalItems,
      day: days,
      week: weeks,
      month: months,
    }

    // Get the price of the current item
    const itemPrice = Number(updatedCheckedItems[listIndex]?.price)

    // Update the technologyCards
    setTechnologyCards((prevTechnologyCards: TechnologyCard[]) =>
      prevTechnologyCards.map((card) =>
        card === technologyCard
          ? {
              ...card,
              technologyCards: card?.technologyCards?.map((item, index) => ({
                ...item,
                totalPrice:
                  listIndex === index
                    ? newTotalItems * itemPrice
                    : item.totalPrice,
                totalItems:
                  listIndex === index ? newTotalItems : (item?.totalItems ?? 0),
                day: listIndex === index ? days : (item?.day ?? 0),
                week: listIndex === index ? weeks : (item?.week ?? 0),
                month: listIndex === index ? months : (item?.month ?? 0),
              })),
            }
          : card
      )
    )

    // Update the checkedItems state
    setCheckedItems(updatedCheckedItems)

    // Update the total price for the specific technologyCard
    if (technologyCard) {
      updateTechnologyCardTotalPrice(cardIndex)
    }
  }

  // compose methods
  const [isComposePopupOpen, setIsComposePopupOpen] = React.useState(false)
  const updateCompose = () => {
    setTechnologyCards((prevTechnologyCards: TechnologyCard[]) =>
      prevTechnologyCards.map((card) =>
        card === technologyCard
          ? {
              ...card,
              compose: {
                heading: compose.heading,
                description: compose.description,
              },
            }
          : card
      )
    )
  }
  const resetCompose = () => {
    setTechnologyCards((prevTechnologyCards: TechnologyCard[]) =>
      prevTechnologyCards.map((card) =>
        card === technologyCard
          ? {
              ...card,
              compose: {
                heading: "",
                description: "",
              },
            }
          : card
      )
    )
  }

  const [theme, setTheme] = React.useState<string | null>("")
  React.useEffect(() => {
    const interval = setInterval(() => {
      const currentTheme = localStorage.getItem("theme")
      setTheme(currentTheme)
    }, 500)

    return () => clearInterval(interval)
  }, [])
  return (
    <>
      <div className=" p-3 h-full break-inside-avoid">
        <div className="w-full relative bg-testimonial shadow-xl rounded-br-xl rounded-t-xl">
          <div className="absolute w-[20%] bg-transparent top-0 bottom-0 right-0 border-l"></div>
          <div className="flex items-center">
            <div className="w-[80%] bg-[#7B68EC] rounded-tl-xl py-4 px-3">
              <p className="text-white lg:text-lg text-sm font-bold">
                {technologyCard?.title}
              </p>
            </div>
            <div className="w-[20%] px-3 py-5 border-b border-l">
              <p className="h-full text-sm font-semibold text-black">Pricing</p>
            </div>
          </div>
          <div className="w-[80%] px-3 pt-2">
            <p className="text-purple text-xs font-semibold">
              {technologyCard?.description}
            </p>
            <p className="text-purple pt-4 text-lg font-semibold text-nowrap">
              {technologyCard?.optionalDescription}
            </p>
            <div className="flex gap-1 justify-center">
              <p className="text-purple relative top-2 text-center text-xs font-light">
                {technologyCard?.optionalBottomText}
              </p>
              <p className="text-purple text-center text-lg font-semibold">
                {technologyCard?.optionalDesPrice}
              </p>
            </div>
          </div>
          {technologyCard?.technologyCards?.some(
            (innerItem) =>
              innerItem?.sideText?.includes("day") ||
              innerItem?.sideText?.includes("week") ||
              innerItem?.sideText?.includes("month")
          ) || false ? (
            <div className="flex w-[80%] justify-end">
              <p className="text-purple text-xs font-semibold px-2">Hours</p>
            </div>
          ) : null}

          {technologyCard?.technologyCards?.map(
            (innerItem: TechnologyCardList, index) => (
              <div key={index}>
                {/* {innerItem.isHidden !== true && ( */}
                <div
                  className={`flex relative justify-between items-center ${
                    innerItem?.isHidden ? "cursor-not-allowed opacity-30" : ""
                  }`}
                >
                  <div className="pl-3 pr-2 pt-2 w-[80%] flex items-center justify-between">
                    <div className="flex my-1 gap-2 items-center">
                      <div
                        onClick={() => handleToggleItemCheck(cardIndex, index)}
                        className="checkbox cursor-pointer relative border border-[#7B68EC] w-5 h-5 rounded"
                      >
                        {innerItem?.isChecked && (
                          <div className="checked bg-[#7B68EC] text-xs absolute flex items-center justify-center inset-0 w-full text-center text-white">
                            ✓
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="flex text-nowrap gap-1">
                          <p className="text-black text-nowrap text-xs">
                            {innerItem?.title}
                          </p>
                          <p className="text-[#7B68EC] font-semibold text-xs">
                            {innerItem?.sideText?.includes("day")
                              ? innerItem.day + " day"
                              : innerItem?.sideText?.includes("week")
                                ? innerItem.week + " week"
                                : innerItem?.sideText?.includes("Month")
                                  ? innerItem.month + " month"
                                  : innerItem?.sideText?.includes("words")
                                    ? innerItem.words + " words"
                                    : innerItem?.sideText?.includes("seo")
                                      ? innerItem.seo + " days"
                                      : innerItem?.sideText?.includes(
                                            "marketing"
                                          )
                                        ? innerItem.marketing + " days"
                                        : ""}
                          </p>
                        </div>
                        <p className="text-black text-xs">
                          {innerItem?.bottomText}
                        </p>
                      </div>
                    </div>
                    {innerItem?.isParent === false && (
                      <div className="flex items-center justify-center gap-1">
                        <div
                          onClick={() =>
                            decrease(index, innerItem?.sideText ?? "")
                          }
                          className="flex cursor-pointer items-center justify-center w-4 h-4 rounded-full border border-[#7B68EC]"
                        >
                          <p className="text-black flex text-xs px-1">-</p>
                        </div>
                        <div
                          onClick={() =>
                            increase(index, innerItem?.sideText ?? "")
                          }
                          className="flex cursor-pointer items-center justify-center w-4 h-4 rounded-full bg-[#7B68EC] border border-[#7B68EC]"
                        >
                          <p className="text-white flex text-xs px-1">+</p>
                        </div>
                        {innerItem?.sideText?.includes("day") ||
                        innerItem?.sideText?.includes("week") ||
                        innerItem?.sideText?.includes("Month") ||
                        innerItem?.sideText?.includes("seo") ||
                        innerItem?.sideText?.includes("words") ||
                        innerItem?.sideText?.includes("marketing") ? (
                          <div className="flex w-10 h-5 items-center justify-center rounded bg-[#FFD85B]">
                            <p className="flex text-xs px-2">
                              {checkedItems[index]?.totalItems}
                            </p>
                          </div>
                        ) : (
                          <input
                            type="number"
                            min={1}
                            max={300}
                            value={checkedItems[index]?.totalItems || 0}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value, 10)
                              if (!isNaN(newValue) && newValue >= 0) {
                                updateTotalItems(
                                  index,
                                  newValue,
                                  innerItem?.sideText ?? ""
                                )
                              }
                            }}
                            className="flex w-10 h-5 text-center border rounded bg-[#FFD85B] text-xs"
                          />
                        )}
                      </div>
                    )}
                    {innerItem?.info !== null && (
                      <div>
                        <p className="pt-2 text-black">⌃</p>
                      </div>
                    )}
                  </div>
                  <div className="w-[20%] pt-2">
                    {innerItem.price && (
                      <p className="text-black font-bold text-center">
                        $
                        {(innerItem?.totalItems ?? 0) *
                          (Number(innerItem?.price) ?? 0)}
                      </p>
                    )}
                  </div>
                </div>
                {/* // )} */}
                {innerItem?.cardSubSectionOptions &&
                  innerItem?.cardSubSectionOptions?.length > 0 && (
                    <div
                      className={`pl-5 pt-2 ${
                        innerItem.isChecked !== true
                          ? "opacity-30 cursor-not-allowed relative"
                          : "opacity-100"
                      }`}
                      key={index}
                    >
                      {innerItem.isChecked !== true && (
                        <div className="absolute inset-0"></div>
                      )}
                      <PricingCheckBox
                        updateTechnologyCardTotalPrice={
                          updateTechnologyCardTotalPrice
                        }
                        radioIndex={index}
                        cardIndex={cardIndex}
                        innerCheckBoxes={innerItem?.cardSubSectionOptions}
                        technologyCard={technologyCard}
                        setTechnologyCards={setTechnologyCards}
                      />
                    </div>
                  )}
              </div>
            )
          )}
          <div>
            <div className="px-3 pt-6 w-[80%] flex items-center justify-between">
              <div>
                <p className="text-gradient-tertiary text-xs">
                  We offered One Month free support after completion the
                  development
                </p>
              </div>
            </div>
          </div>
          <div className="flex py-2 items-center">
            <div className="w-[80%] rounded-tl-xl px-2">
              <p className="text-[#7B68EC]">Subtotal of this Box</p>
            </div>
            <div className="w-[20%] bg-[#7B68EC] rounded-s-full text-center px-3 py-1">
              <p className="h-full text-lg text-white">
                ${technologyCard?.totalPrice}
              </p>
            </div>
          </div>
          {technologyCard?.compose.description === "" &&
            technologyCard?.compose.heading === "" && (
              <div className="flex bg-[#9F69FF38] relative z-10 rounded-br-xl items-center">
                <div className="w-[70%] rounded-tl-xl px-2">
                  <p className="text-[#7B68EC] text-xs">
                    If you want anything out of box or any recommendation.
                  </p>
                </div>
                <button
                  onClick={() => setIsComposePopupOpen(true)}
                  className="w-[30%] bg-[#7B68EC] hover:opacity-80 flex items-center gap-2 rounded-br-xl text-center px-3 py-3"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  >
                    <path
                      d="M0.742946 17.0414C0.646707 17.0424 0.551218 17.0244 0.461976 16.9884C0.372734 16.9523 0.291499 16.899 0.222946 16.8314C0.14435 16.7538 0.0838742 16.6598 0.0458429 16.556C0.00781162 16.4523 -0.00683627 16.3415 0.00294639 16.2314L0.312946 12.3914C0.32624 12.2131 0.404523 12.0459 0.532946 11.9214L11.5929 0.861434C12.2098 0.285291 13.0293 -0.0238132 13.8729 0.00143341C14.7113 0.00793838 15.5158 0.333298 16.1229 0.911433C16.7053 1.50944 17.0402 2.30539 17.0606 3.13986C17.081 3.97433 16.7854 4.78569 16.2329 5.41143L5.16295 16.5214C5.03905 16.6449 4.87682 16.7225 4.70295 16.7414L0.812947 17.0914L0.742946 17.0414ZM1.74295 12.7814L1.53295 15.5214L4.27295 15.2714L15.1729 4.35143C15.3855 4.09991 15.5213 3.79267 15.5644 3.46621C15.6074 3.13975 15.5558 2.8078 15.4158 2.50978C15.2757 2.21177 15.0531 1.96022 14.7743 1.78502C14.4955 1.60982 14.1722 1.51834 13.8429 1.52143C13.6265 1.50711 13.4094 1.53696 13.2048 1.60916C13.0002 1.68136 12.8125 1.79441 12.6529 1.94143L1.74295 12.7814Z"
                      fill="white"
                    />
                  </svg>
                  <p className="h-full text-sm text-white">Compose</p>
                </button>
              </div>
            )}
        </div>
      </div>
      {isComposePopupOpen && (
        <div
          className={`h-screen overflow-y-scroll fixed grid-container inset-0 z-40 w-full flex items-center justify-center ${theme == "glassy" ? "bg-compose" : theme === "light" ? "bg-[#0000007D]" : theme === "dark" ? "bg-[#0000008F]" : "bg-compose"}`}
        >
          <div
            className={`backdrop-blur-xl bg-[#FFFFFF5E] my-3 rounded-xl popup-bg p-5 lg:col-span-8 col-span-full mx-2 lg:col-start-5`}
          >
            <div>
              <div className="flex justify-end w-full">
                <button
                  onClick={() => {
                    setIsComposePopupOpen(false)
                    resetCompose()
                  }}
                  className="p-2 w-fit hover:rotate-180 text-black transition duration-300 cursor-pointer text-xl font-bold"
                >
                  X
                </button>
              </div>
              <p className="font-semibold text-black text-xl">Compose</p>
              <div className="mt-8">
                <div className="w-full">
                  <p className="text-black lg:text-lg text-xs font-semibold pb-2">
                    Heading
                  </p>
                </div>
                <div className="w-full xl:shadow-none xl:bg-[unset] xl:py-0 py-3 px-3 xl:px-0 xl:rounded-[0px] rounded-xl">
                  <input
                    onChange={(e) => {
                      setCompose({
                        ...compose,
                        heading: e.target.value,
                        description: compose?.description ?? "",
                      })
                      updateCompose()
                    }}
                    className={`bg-input bg-glass w-full pl-3 pr-3 py-3 font-light rounded-md text-sm outline-none text-black ${theme == "glassy" ? "border border-black !bg-transparent" : theme === "light" ? "" : theme === "dark" ? "" : "border border-black !bg-transparent"}`}
                    placeholder="Enter Heading"
                    type="text"
                  />
                </div>
              </div>
              <div className="mt-5">
                <div className="w-full pb-4">
                  <p className="text-black lg:text-lg text-xs font-semibold">
                    Description
                  </p>
                  <p className="text-gray text-xs pt-1">
                    Project Description, goals and criteria
                  </p>
                </div>
                <div className="w-full xl:shadow-none xl:bg-[unset] xl:py-0 py-3 px-3 xl:px-0 xl:rounded-[0px] rounded-xl">
                  <textarea
                    onChange={(e) => {
                      setCompose({
                        ...compose,
                        heading: compose?.heading ?? "",
                        description: e.target.value,
                      })
                      updateCompose()
                    }}
                    className={`bg-input bg-glass w-full pl-3 pr-3 py-3 font-light rounded-md text-sm outline-none text-black ${theme == "glassy" ? "border border-black !bg-transparent" : theme === "light" ? "" : theme === "dark" ? "" : "border border-black !bg-transparent"}`}
                    placeholder="Enter Project Details"
                    rows={5}
                  />
                </div>
              </div>
              <button
                onClick={() => setIsComposePopupOpen(false)}
                className={`mt-3 hover:opacity-80 gap-2 rounded-xl text-center px-3 py-3 w-full ${theme == "glassy" ? "bg-[#7B68EC] text-white" : theme === "light" ? "bg-btn text-black" : theme === "dark" ? "bg-btn text-white" : "bg-[#7B68EC] text-white"}`}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
